import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { Label } from '../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Loader2, FileText, MessageSquare, Download, Eye } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoiceView = ({ invoice, onClose }) => {
  const printRef = useRef();

  const handlePrint = () => {
    const content = printRef.current;
    const win = window.open('', '_blank');
    win.document.write(`
      <html><head><title>Invoice ${invoice.invoice_id}</title>
      <style>
        body { font-family: 'Georgia', serif; padding: 40px; color: #1a1a1a; max-width: 800px; margin: 0 auto; }
        .header { display: flex; justify-content: space-between; border-bottom: 2px solid #8b4513; padding-bottom: 20px; margin-bottom: 30px; }
        .store-name { font-size: 24px; font-weight: bold; color: #c7738e; }
        .invoice-title { font-size: 14px; color: #666; text-transform: uppercase; letter-spacing: 2px; }
        .invoice-id { font-size: 18px; font-weight: bold; }
        .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin-bottom: 30px; }
        .info-block h4 { font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #888; margin: 0 0 4px 0; }
        .info-block p { margin: 0; font-size: 14px; }
        table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
        th { text-align: left; padding: 10px; border-bottom: 1px solid #ddd; font-size: 11px; text-transform: uppercase; letter-spacing: 1px; color: #888; }
        td { padding: 10px; border-bottom: 1px solid #eee; font-size: 14px; }
        .total-row td { font-weight: bold; border-top: 2px solid #c7738e; border-bottom: none; font-size: 16px; }
        .footer { text-align: center; margin-top: 40px; padding-top: 20px; border-top: 1px solid #eee; font-size: 12px; color: #888; }
        .badge { display: inline-block; padding: 2px 8px; border-radius: 4px; font-size: 11px; font-weight: bold; text-transform: uppercase; }
        .badge-paid { background: #dcfce7; color: #166534; }
        .badge-pending { background: #fef9c3; color: #854d0e; }
      </style></head><body>${content.innerHTML}
      <script>window.print();window.close();</script></body></html>
    `);
  };

  return (
    <div>
      <div ref={printRef}>
        <div className="header" style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '2px solid #c7738e', paddingBottom: '16px', marginBottom: '24px' }}>
          <div>
            <div style={{ fontSize: '24px', fontWeight: 'bold', color: '#c7738e' }}>{invoice.store_name}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{invoice.store_email}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '2px', color: '#888' }}>Invoice</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{invoice.invoice_id}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{new Date(invoice.date).toLocaleDateString()}</div>
          </div>
        </div>

        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
          <div>
            <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: '#888', marginBottom: '4px' }}>Bill To</div>
            <div style={{ fontSize: '14px', fontWeight: '500' }}>{invoice.customer_name}</div>
            <div style={{ fontSize: '13px', color: '#666' }}>{invoice.customer_email}</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: '#888', marginBottom: '4px' }}>Payment</div>
            <div style={{ fontSize: '14px' }}>{invoice.payment_method?.toUpperCase()}</div>
            <span className={`inline-block px-2 py-0.5 rounded text-xs font-bold uppercase ${invoice.payment_status === 'paid' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
              {invoice.payment_status}
            </span>
          </div>
        </div>

        <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '16px' }}>
          <thead>
            <tr>
              <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: '#888' }}>Item</th>
              <th style={{ textAlign: 'center', padding: '8px', borderBottom: '1px solid #ddd', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: '#888' }}>Qty</th>
              <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #ddd', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: '#888' }}>Price</th>
              <th style={{ textAlign: 'right', padding: '8px', borderBottom: '1px solid #ddd', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '1px', color: '#888' }}>Total</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items?.map((item, i) => (
              <tr key={i}>
                <td style={{ padding: '8px', borderBottom: '1px solid #eee', fontSize: '14px' }}>{item.title}</td>
                <td style={{ padding: '8px', borderBottom: '1px solid #eee', fontSize: '14px', textAlign: 'center' }}>{item.quantity}</td>
                <td style={{ padding: '8px', borderBottom: '1px solid #eee', fontSize: '14px', textAlign: 'right' }}>${item.price?.toFixed(2)}</td>
                <td style={{ padding: '8px', borderBottom: '1px solid #eee', fontSize: '14px', textAlign: 'right' }}>${(item.price * item.quantity).toFixed(2)}</td>
              </tr>
            ))}
            <tr>
              <td colSpan={3} style={{ padding: '10px 8px', fontWeight: 'bold', borderTop: '2px solid #c7738e', fontSize: '16px', textAlign: 'right' }}>Total</td>
              <td style={{ padding: '10px 8px', fontWeight: 'bold', borderTop: '2px solid #c7738e', fontSize: '16px', textAlign: 'right' }}>${invoice.total?.toFixed(2)}</td>
            </tr>
          </tbody>
        </table>

        <div style={{ textAlign: 'center', marginTop: '32px', paddingTop: '16px', borderTop: '1px solid #eee', fontSize: '12px', color: '#888' }}>
          Thank you for your purchase from {invoice.store_name}
        </div>
      </div>

      <div className="flex justify-end gap-3 mt-6">
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button onClick={handlePrint} data-testid="print-invoice-btn">
          <Download className="h-4 w-4 mr-2" />
          Print / Save PDF
        </Button>
      </div>
    </div>
  );
};

export const AdminOrdersPage = () => {
  const { token } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [invoiceData, setInvoiceData] = useState(null);
  const [invoiceOpen, setInvoiceOpen] = useState(false);

  const getHeaders = () => ({ Authorization: `Bearer ${token}` });

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API}/admin/orders?page=${page}&limit=20`, {
        headers: getHeaders(), withCredentials: true
      });
      setOrders(response.data.orders || []);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error('Error fetching orders:', error);
    }
    setLoading(false);
  };

  useEffect(() => { fetchOrders(); }, [token, page]);

  const handleStatusChange = async (orderId, status) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/status?status=${status}`, {}, {
        headers: getHeaders(), withCredentials: true
      });
      toast.success('Order status updated');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleViewInvoice = async (orderId) => {
    try {
      const res = await axios.get(`${API}/admin/orders/${orderId}/invoice`, {
        headers: getHeaders(), withCredentials: true
      });
      setInvoiceData(res.data);
      setInvoiceOpen(true);
    } catch (error) {
      toast.error('Failed to load invoice');
    }
  };

  const handleWhatsAppNotify = async (orderId) => {
    try {
      const res = await axios.post(`${API}/admin/orders/${orderId}/whatsapp-notify`, {}, {
        headers: getHeaders(), withCredentials: true
      });
      if (res.data.success) {
        toast.success('WhatsApp notification sent');
      } else {
        toast.error(res.data.error || 'Failed to send notification');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send WhatsApp notification');
    }
  };

  const handleToggle = async (orderId, field, value) => {
    try {
      await axios.put(`${API}/admin/orders/${orderId}/toggles?${field}=${value}`, {}, {
        headers: getHeaders(), withCredentials: true
      });
      toast.success('Updated');
      fetchOrders();
    } catch (error) {
      toast.error('Failed to update');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  const totalPages = Math.ceil(total / 20);

  return (
    <div data-testid="admin-orders">
      <h1 className="font-heading text-3xl font-bold mb-8">Orders</h1>

      <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl">Invoice</DialogTitle>
          </DialogHeader>
          {invoiceData && <InvoiceView invoice={invoiceData} onClose={() => setInvoiceOpen(false)} />}
        </DialogContent>
      </Dialog>

      <Card className="border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Order ID</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead>Items</TableHead>
                <TableHead>Total</TableHead>
                <TableHead>Payment</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Access</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center py-8 text-muted-foreground">
                    No orders yet
                  </TableCell>
                </TableRow>
              ) : (
                orders.map((order) => (
                  <TableRow key={order.order_id}>
                    <TableCell className="font-mono text-sm">
                      #{order.order_id.slice(-8)}
                    </TableCell>
                    <TableCell className="text-sm">{order.user_id?.slice(-8)}</TableCell>
                    <TableCell>
                      {order.items?.length} item{order.items?.length > 1 ? 's' : ''}
                    </TableCell>
                    <TableCell className="font-medium">${order.total?.toFixed(2)}</TableCell>
                    <TableCell>
                      <Badge variant={order.payment_status === 'paid' ? 'default' : 'secondary'}>
                        {order.payment_status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Select value={order.status} onValueChange={(value) => handleStatusChange(order.order_id, value)}>
                        <SelectTrigger className="w-[120px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="pending">Pending</SelectItem>
                          <SelectItem value="confirmed">Confirmed</SelectItem>
                          <SelectItem value="processing">Processing</SelectItem>
                          <SelectItem value="shipped">Shipped</SelectItem>
                          <SelectItem value="delivered">Delivered</SelectItem>
                          <SelectItem value="cancelled">Cancelled</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-1">
                        <div className="flex items-center gap-1.5">
                          <Switch
                            checked={order.download_enabled !== false}
                            onCheckedChange={(v) => handleToggle(order.order_id, 'download_enabled', v)}
                            className="scale-75"
                          />
                          <span className="text-xs text-muted-foreground">DL</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <Switch
                            checked={order.embed_enabled !== false}
                            onCheckedChange={(v) => handleToggle(order.order_id, 'embed_enabled', v)}
                            className="scale-75"
                          />
                          <span className="text-xs text-muted-foreground">Embed</span>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground text-sm">
                      {new Date(order.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" onClick={() => handleViewInvoice(order.order_id)} title="View Invoice" data-testid={`invoice-${order.order_id}`}>
                          <FileText className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => handleWhatsAppNotify(order.order_id)} title="Send WhatsApp" data-testid={`whatsapp-${order.order_id}`}>
                          <MessageSquare className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-6">
          <Button variant="outline" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}>Previous</Button>
          <span className="text-sm text-muted-foreground px-4">Page {page} of {totalPages}</span>
          <Button variant="outline" onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}>Next</Button>
        </div>
      )}
    </div>
  );
};

export const AdminUsersPage = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const response = await axios.get(`${API}/admin/users`, {
          headers: { Authorization: `Bearer ${token}` }, withCredentials: true
        });
        setUsers(response.data.users || []);
      } catch (error) {
        console.error('Error fetching users:', error);
      }
      setLoading(false);
    };
    fetchUsers();
  }, [token]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div data-testid="admin-users">
      <h1 className="font-heading text-3xl font-bold mb-8">Users</h1>
      <Card className="border-border">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>User</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>Joined</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((user) => (
                <TableRow key={user.user_id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-muted flex items-center justify-center">
                          {user.name?.charAt(0).toUpperCase()}
                        </div>
                      )}
                      <span className="font-medium">{user.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>{user.role}</Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {new Date(user.created_at).toLocaleDateString()}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};
